/**
 * todo.list adapter — ClientAdapter for todo.list tiles.
 *
 * todo.list is a noFetch, clientDirect tile. Params ARE the storage:
 * { items: TodoItem[] } is the ordered list, edited via configureTile only.
 * Data is computed locally from params (no network, no upstream).
 *
 * GLANCE RULINGS (glance-grammar-spec §1.3 / D-015):
 *   primary : String(remainingCount) — the number of unchecked tasks.
 *             SPECIAL CASES:
 *             - Empty list (total=0): primary = 'Add tasks' — invites first item.
 *               This is 9 chars, clamped to 8 by the central clamp → 'Add task'.
 *               Decision: use '0 left' for empty list (6 chars, fits 1×1) so
 *               the number read is consistent and the cta lives in the detail sheet.
 *               REVISED: 'No tasks' (8 chars exact) is more calm and explicit.
 *               FINAL: 'No tasks' (8 chars, exactly at limit) for empty list.
 *             - All done (remaining=0, total>0): primary = 'Done', glanceState = 'positive'.
 *             - Otherwise: primary = String(remainingCount), e.g. '3'.
 *   delta   : nextItemText (first undone item, in order). Null when all done or empty.
 *             The central clamp guarantees ≤20 chars.
 *   state   : 'positive' when all done (remaining=0 AND total>0), 'neutral' otherwise.
 *
 * Immutability: the exported adapter object is frozen; glanceView returns new.
 */
import { z } from 'zod';
import {
  todoParamsSchema,
  deriveCacheKey,
  type TodoParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import { computeTodoData, type TodoData } from './compute.js';

/** Zod shape for TodoData — mirrors the fields TodoData declares. */
export const todoDataShape = z.object({
  doneCount: z.number().int().min(0),
  totalCount: z.number().int().min(0),
  remainingCount: z.number().int().min(0),
  nextItemText: z.string(),
});

/**
 * Map TodoData + GlanceMap → GlanceViewModel.
 *
 * Ruling: see file docblock. The central clamp guarantees ≤8 primary, ≤20 delta.
 */
export function todoGlanceView(data: TodoData, _glance: GlanceMap): GlanceViewModel {
  const { remainingCount, totalCount, nextItemText } = data;

  let primary: string;
  let delta: string | null;
  let glanceState: 'positive' | 'neutral';

  if (totalCount === 0) {
    // Empty list: calm invitation, no delta.
    primary = 'No tasks';
    delta = null;
    glanceState = 'neutral';
  } else if (remainingCount === 0) {
    // All done: positive celebration.
    primary = 'Done';
    delta = null;
    glanceState = 'positive';
  } else {
    // Normal: count remaining, show next task as context.
    primary = String(remainingCount);
    delta = nextItemText || null;
    glanceState = 'neutral';
  }

  return {
    dataState: 'ok',
    glanceState,
    slots: { primary, delta },
    label: null,
    fetchedAt: Date.now(),
  };
}

export const todoAdapter: ClientAdapter<TodoParams, TodoData> = Object.freeze({
  type: 'todo.list' as const,
  provider: 'local' as const,
  tier: 'free' as const,
  ttlSec: 0,
  paramsSchema: todoParamsSchema,
  dataShape: todoDataShape,

  cacheKey: (params: TodoParams): string =>
    deriveCacheKey({ type: 'todo.list', provider: 'local', params }),

  glanceView: (data: TodoData, glance: GlanceMap): GlanceViewModel =>
    todoGlanceView(data, glance),
});

/** Compute TodoData from TodoParams (exported for use-tile-view-model). */
export { computeTodoData };
